import NextLink from 'next/link'
import type { ComponentProps, ReactNode } from 'react'

/**
 * A link the CMS produced, routed the way its destination deserves.
 *
 * The API serves three kinds of URL in the same field: an entry path, a term
 * path, and whatever an editor typed into a custom menu item — which may be
 * anywhere. `next/link` is right for the first two and wrong for the third: it
 * prefetches and then client-navigates, and doing that to another origin is
 * either a wasted request or a broken one.
 *
 * So the destination decides. A path is a route; anything else is a document
 * somewhere else, and gets an anchor with the `rel` that implies.
 */
export function isInternal(href: string): boolean {
    // `//host` is protocol-relative: it looks like a path and leaves the site.
    return href.startsWith('/') && !href.startsWith('//')
}

type Props = {
    href: string
    // Optional, because a component that takes a `render` element — Base UI's
    // Button, Badge and NavigationMenuLink all do — clones this one and
    // supplies the children itself.
    children?: ReactNode
    target?: string
} & Omit<ComponentProps<'a'>, 'href' | 'children' | 'target'>

export function CmsLink({ href, children, target, ...props }: Props) {
    // A link opening a new tab leaves this document either way, so it is an
    // anchor even when it points at this site.
    if (!isInternal(href) || target === '_blank') {
        return (
            <a href={href} target={target} rel={target === '_blank' ? 'noreferrer' : undefined} {...props}>
                {children}
            </a>
        )
    }

    return (
        <NextLink href={href} {...props}>
            {children}
        </NextLink>
    )
}
